import React, { useEffect, useState } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Rating, Select, Stack, TextField, Typography,
} from "@mui/material";
import { formatDateTime, getMessage, useLocale } from "../i18n/locale";
import { reviewService } from "../services/resources.service";

const CATEGORIES = ["professionalism", "punctuality", "sound", "crowd"];

export default function EventReviewDialog({ open, onClose, targetType, targetId, onCreated }) {
  const { locale } = useLocale();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [ratings, setRatings] = useState(Object.fromEntries(CATEGORIES.map((key) => [key, 0])));
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    reviewService.eligibility(targetType, targetId)
      .then(({ events: nextEvents }) => {
        setEvents(nextEvents);
        setEventId(nextEvents[0]?.id || "");
      })
      .catch((err) => setError(err.message));
  }, [open, targetId, targetType]);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const review = await reviewService.create({ eventId, targetType, targetId, ratings, text });
      onCreated(review);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const valid = eventId && text.trim() && CATEGORIES.every((key) => ratings[key] > 0);
  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{getMessage(locale, targetType === "artist" ? "reviewArtistTitle" : "reviewVenueTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!error && events.length === 0 ? (
            <Alert severity="info">{getMessage(locale, "reviewNoEligibleEvent")}</Alert>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel>{getMessage(locale, "reviewEventLabel")}</InputLabel>
                <Select value={eventId} label={getMessage(locale, "reviewEventLabel")} onChange={(e) => setEventId(e.target.value)}>
                  {events.map((event) => (
                    <MenuItem key={event.id} value={event.id}>
                      {event.title} · {formatDateTime(event.endedAt, { locale })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {CATEGORIES.map((key) => (
                <Stack key={key} direction="row" justifyContent="space-between" alignItems="center">
                  <Typography>{getMessage(locale, `reviewCategory${key}`)}</Typography>
                  <Rating value={ratings[key]} onChange={(_, value) => setRatings((old) => ({ ...old, [key]: value || 0 }))} />
                </Stack>
              ))}
              <TextField multiline minRows={3} inputProps={{ maxLength: 1000 }} label={getMessage(locale, "reviewTextLabel")} value={text} onChange={(e) => setText(e.target.value)} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{getMessage(locale, "reviewCancel")}</Button>
        <Button variant="contained" disabled={!valid || submitting} onClick={submit}>
          {getMessage(locale, submitting ? "reviewSubmitting" : "reviewSubmit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
